// src/scenes/finance-reports/index.jsx

import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Modal,
    IconButton,
    Card,
    CardContent,
    CardActionArea,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme";

/* ---------- Report Components ---------- */
import OperatingStatement from "../../component-files/reports/OperatingStatement";
import FinancialInsights from "../../component-files/reports/FinancialInsights";
import InventoryValueReport from "../../component-files/reports/InventoryValueReport";
import MonthlyReportbyVendor from "../../component-files/reports/MonthlyReportbyVendor";
import FederalReimbursementReport from "../../component-files/reports/FederalReimbursementReport";
import BudgetAnalysisbySite from "../../component-files/reports/BudgetAnalysisbySite";

/* ---------- Icons ---------- */
import SummarizeIcon from "@mui/icons-material/Summarize";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InsightsIcon from "@mui/icons-material/Insights";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AnalyticsIcon from "@mui/icons-material/Analytics";

/* ---------- Data ---------- */
const reports = [
    {
        key: "operating",
        title: "Operating Statement",
        description: "View detailed operating statements.",
        component: <OperatingStatement />,
        section: "standard",
        Icon: SummarizeIcon,
        color: "#1565C0"
    },
    {
        key: "inventoryValue",
        title: "Inventory Value Report",
        description: "View inventory value details by school district.",
        component: <InventoryValueReport />,
        section: "standard",
        Icon: Inventory2Icon,
        color: "#6A1B9A"
    },
    {
        key: "federalReimbursement",
        title: "Federal Reimbursement Report",
        description: "View federal reimbursement details.",
        component: <FederalReimbursementReport />,
        section: "standard",
        Icon: AccountBalanceIcon,
        color: "#C62828"
    },
    {
        key: "financialInsights",
        title: "Financial Insights",
        description: "Analyze financial performance using AI-powered insights.",
        component: <FinancialInsights />,
        section: "ai",
        Icon: InsightsIcon,
        color: "#EF6C00"
    },
    {
        key: "monthlyReportByVendor",
        title: "Monthly Expense by Vendor Report",
        description: "View monthly expense details grouped by vendor.",
        component: <MonthlyReportbyVendor />,
        section: "ai",
        Icon: StorefrontIcon,
        color: "#2E7D32"
    },
    {
        key: "budgetAnalysis",
        title: "Budget Analysis by Site",
        description: "View AI-driven budget analysis for each site.",
        component: <BudgetAnalysisbySite />,
        section: "ai",
        Icon: AnalyticsIcon,
        color: "#283593"
    },
];

export default function FinanceReports() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [openReport, setOpenReport] = useState(null);

    const getCardStyles = (color) => ({
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.5)}, ${alpha(theme.palette.background.paper, 0.3)})`,
        backdropFilter: "blur(24px)",
        border: `1px solid ${alpha(color, 0.2)}`,
        color: theme.palette.text.primary,
        boxShadow: `
            0 6px 24px ${alpha(color, 0.12)},
            0 0 0 1px ${alpha(color, 0.08)}
        `,
        transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
        "&:active": {
            transform: "scale(0.97)",
            transition: "all 0.1s ease",
        },
        "&:hover": {
            transform: "translateY(-10px) scale(1.02)",
            border: `1px solid ${alpha(color, 0.5)}`,
            boxShadow: `
                0 20px 56px ${alpha(color, 0.28)},
                0 0 100px ${alpha(color, 0.35)},
                0 0 0 1px ${alpha(color, 0.25)}
            `,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        },
    });

    const centerWrap = {
        height: "100%",
        display: "grid",
        gridTemplateRows: "1fr auto auto auto",
        alignItems: "center",
        justifyItems: "center",
        p: { xs: 2, sm: 2.5 },
        gap: 1.2,
        textAlign: "center",
    };

    const ReportCard = ({ report }) => {
        const IconComp = report.Icon || SummarizeIcon;
        const color = report.color;

        return (
            <Grid item xs={12} sm={6} md={2.4} key={report.key}>
                <Card sx={getCardStyles(color)} aria-label={report.title}>
                    <Box sx={{ aspectRatio: "1 / 1", position: "relative" }}>
                        <CardActionArea
                            onClick={() => setOpenReport(report.key)}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                borderRadius: 3,
                                "&.Mui-focusVisible": {
                                    outline: `3px solid ${alpha(color, 0.7)}`,
                                    outlineOffset: 2,
                                },
                                "& .MuiTouchRipple-root .MuiTouchRipple-child": {
                                    backgroundColor: alpha(color, 0.4),
                                },
                            }}
                        >
                            <CardContent sx={centerWrap}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        display: "grid",
                                        placeItems: "center",
                                        width: "min(22vmin, 110px)",
                                        height: "min(22vmin, 110px)",
                                        borderRadius: 3,
                                        background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.05)})`,
                                        backdropFilter: "blur(12px)",
                                        border: `1px solid ${alpha(color, 0.25)}`,
                                        boxShadow: `
                                            0 12px 40px ${alpha(color, 0.25)},
                                            0 0 80px ${alpha(color, 0.2)},
                                            inset 0 1px 0 ${alpha("#fff", 0.1)}
                                        `,
                                        transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                        "& svg": {
                                            fontSize: "clamp(56px, 10vmin, 80px)",
                                            color: color,
                                            filter: `drop-shadow(0 4px 20px ${alpha(color, 0.4)})`,
                                            transition: "all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                        },
                                        ".MuiCardActionArea-root:hover &": {
                                            transform: "translateY(-6px) scale(1.08)",
                                            border: `1px solid ${alpha(color, 0.5)}`,
                                            background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
                                            boxShadow: `
                                                0 16px 56px ${alpha(color, 0.4)},
                                                0 0 120px ${alpha(color, 0.35)},
                                                inset 0 1px 0 ${alpha("#fff", 0.15)}
                                            `,
                                        },
                                        ".MuiCardActionArea-root:hover & svg": {
                                            transform: "scale(1.1)",
                                            filter: `drop-shadow(0 6px 28px ${alpha(color, 0.6)})`,
                                        },
                                    }}
                                >
                                    <IconComp />
                                </Box>

                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 800,
                                        letterSpacing: 0.2,
                                        fontSize: { xs: 14, sm: 16 },
                                        transition: "all 0.3s ease",
                                        ".MuiCardActionArea-root:hover &": {
                                            color: color,
                                            transform: "scale(1.04)",
                                        },
                                    }}
                                >
                                    {report.title}
                                </Typography>

                                <Typography
                                    sx={{
                                        maxWidth: 400,
                                        opacity: 0.8,
                                        fontSize: { xs: 11, sm: 12 },
                                        lineHeight: 1.3,
                                        px: { xs: 0.5, sm: 0 },
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        WebkitLineClamp: 2,
                                        overflow: "hidden",
                                        color: theme.palette.text.secondary,
                                        transition: "all 0.3s ease",
                                        ".MuiCardActionArea-root:hover &": {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    {report.description}
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        letterSpacing: 1,
                                        fontSize: 10,
                                        px: 2.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        background: alpha(color, 0.12),
                                        border: `1px solid ${alpha(color, 0.25)}`,
                                        color: color,
                                        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                                        ".MuiCardActionArea-root:hover &": {
                                            background: alpha(color, 0.22),
                                            border: `1px solid ${alpha(color, 0.5)}`,
                                            transform: "scale(1.06)",
                                            boxShadow: `0 4px 20px ${alpha(color, 0.35)}`,
                                        },
                                    }}
                                >
                                    Open
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Box>
                </Card>
            </Grid>
        );
    };

    const ReportGrid = ({ sectionKey, label, gradient }) => (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ mb: 3, pb: 2, borderBottom: `2px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 900,
                        background: gradient,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: 24, sm: 32 },
                    }}
                >
                    {label}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {reports
                    .filter((r) => r.section === sectionKey)
                    .map((r) => (
                        <ReportCard key={r.key} report={r} />
                    ))}
            </Grid>
        </Box>
    );

    return (
        <Box
            sx={{
                p: { xs: 3, sm: 4, md: 5 },
                minHeight: "100vh",
            }}
        >
            <Box
                sx={{
                    mb: 5,
                    pb: 3,
                    borderBottom: `3px solid ${alpha(theme.palette.divider, 0.2)}`,
                }}
            >
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        background: "linear-gradient(135deg, #1565C0 0%, #00897B 50%, #C62828 100%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        fontSize: { xs: 32, sm: 48 },
                        textAlign: "center",
                    }}
                >
                    Finance Reports
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        mt: 1,
                        textAlign: "center",
                        color: theme.palette.text.secondary,
                        fontSize: { xs: 14, sm: 16 },
                    }}
                >
                    Comprehensive financial reporting and analytics for school nutrition programs
                </Typography>
            </Box>

            <ReportGrid
                sectionKey="standard"
                label="Standard Reports"
                gradient="linear-gradient(135deg, #1565C0 0%, #00897B 100%)"
            />

            <ReportGrid
                sectionKey="ai"
                label="AI & Vendor Reports"
                gradient="linear-gradient(135deg, #EF6C00 0%, #283593 100%)"
            />

            {/* Modal */}
            <Modal
                open={!!openReport}
                onClose={() => setOpenReport(null)}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                    backdropFilter: "blur(8px)",
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 3,
                        boxShadow: `0 24px 80px ${alpha("#000", 0.4)}`,
                        width: "11.9in",
                        height: "15.4in",
                        overflow: "hidden",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: `linear-gradient(135deg, ${alpha(reports.find((r) => r.key === openReport)?.color || "#1565C0", 0.9)}, ${alpha(reports.find((r) => r.key === openReport)?.color || "#1565C0", 0.7)})`,
                            backdropFilter: "blur(20px)",
                            color: "#fff",
                            borderRadius: "12px 12px 0 0",
                            p: 2.5,
                            boxShadow: `0 4px 20px ${alpha(reports.find((r) => r.key === openReport)?.color || "#1565C0", 0.3)}`,
                        }}
                    >
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {reports.find((r) => r.key === openReport)?.title}
                        </Typography>
                        <IconButton
                            onClick={() => setOpenReport(null)}
                            sx={{
                                color: "#fff",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    transform: "scale(1.1)",
                                    backgroundColor: alpha("#fff", 0.1),
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ p: 3, height: "calc(100% - 80px)", overflow: "auto" }}>
                        {reports.find((r) => r.key === openReport)?.component}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}